import { FormMultiSelectField } from 'components/form/FormMultiSelectField';
import { FormPhoneNumberField } from 'components/form/FormPhoneNumberField';
import FormTextField from 'components/form/FormTextField';
import React, { useEffect, useMemo, useState } from 'react';
import { useVerificationContext } from '../helpers/verificationContext';
import { useFormikContext } from 'formik';
import { FormValues } from '../schema';
import { RadioButtons } from 'components/radiobuttons/radiobuttons';
import { SelectOptionRadioButton } from 'components/radiobuttons/SelectOptionRadioButtons';
import UploadField from '../helpers/UploadField';
import Button from 'components/button/Button';
import BackButton from '../helpers/BackButton';
import { FormTextAreaField } from 'components/FormBuilder/FormTextAreaField';
import instance, { ApiResponse } from 'api';
import { endpoints } from 'endpoints.config';
import { SelectOption } from 'components/form/FormSingleSelectField';
import { Toast } from 'helpers/toast';
import { useSelector } from 'react-redux';
import { selectComponentResources } from 'reducers/componentResources';

type CountryItem = {
    name: string;
    isoCode: string;
    id: number;
    personalErrorCode?: string | null;
    businessErrorCode?: string | null;
};

type DropdownOptionsResponse = {
    countries?: CountryItem[];
    productServices?: {
        name: string;
        id: number;
    }[];
};

export const OperationsInformation = () => {
    const { selectOptions } = useVerificationContext();
    const { values, setFieldValue } = useFormikContext<FormValues>();
    const [productServices, setProductServices] = useState<SelectOption[]>([]);
    const { userInfo } = useSelector(selectComponentResources);
    const [isPortlProduct, setIsPortlProduct] = useState<boolean>(false);

    const otherFundsSelected = useMemo(
        () =>
            values.operationsInformation.sourceOfFundsIds &&
            values.operationsInformation.sourceOfFundsIds.some(
                (id) =>
                    id ==
                    (selectOptions?.fundingSources?.find((type) => type.label === 'Other')?.value ??
                        0)
            ),
        [selectOptions?.fundingSources, values.operationsInformation.sourceOfFundsIds]
    );
    useEffect(() => {
        if (!otherFundsSelected) setFieldValue(fieldName('sourceOfFundsOther'), null);
    }, [otherFundsSelected, setFieldValue]);

    const fetchProductServices = async () => {
        instance
            .get<ApiResponse<DropdownOptionsResponse>>(
                endpoints.registration.businessRegistrationDetailsV2
            )
            .then((res) => {
                if (res.data.status === '1') {
                    setProductServices(
                        res.data.details.productServices?.map((service) => ({
                            label: service.name,
                            value: service.id,
                        })) ?? []
                    );
                }
            })
            .catch(() => Toast.openGenericErrorToast());
    };

    useEffect(() => {
        fetchProductServices();
    }, []);

    const checkIsPortlProduct = () => {
        const portlProduct = productServices.find(
            (product) => product.label === 'PORTL (Digital Asset Acquiring)'
        );

        if (portlProduct && userInfo?.productServices) {
            const selectedProductIds = userInfo.productServices.split(',').map((id) => id.trim());
            const isSelected = selectedProductIds.includes(String(portlProduct.value));
            setIsPortlProduct(isSelected);
        } else {
            setIsPortlProduct(false);
        }
    };

    useEffect(() => {
        checkIsPortlProduct();
    }, [productServices]);

    useEffect(() => {
        setFieldValue('operationsInformation.isPortlProduct', isPortlProduct);
    }, [isPortlProduct]);

    return (
        <>
            <BackButton />
            <h2>Entity Operations Information</h2>
            <FormTextField
                field={fieldName('webAddress')}
                label={'Website Address'}
                labelExtraInfo="Optional"
            />
            <div className="FormSideBySide">
                <div className="FormBuilderComponent SideBySide">
                    <FormPhoneNumberField
                        field={fieldName('phoneNumber')}
                        label="Business Phone Number"
                        labelExtraInfo="Optional"
                    />
                </div>
                <FormTextField
                    field={fieldName('supportEmail')}
                    label={'Support Email'}
                    labelExtraInfo="Optional"
                    sideBySide
                />
            </div>
            <RadioButtons
                options={['Yes', 'No', 'NA']}
                fieldname={fieldName('bPubliclyListed')}
                label="Is business a publicly listed company? If so, list exchange symbol and exchange where traded."
            />
            {values.operationsInformation.bPubliclyListed === 'Yes' && (
                <div className="FormSideBySide">
                    <FormTextField
                        field={fieldName('ticker')}
                        label={'Exchange Symbol'}
                        sideBySide
                        tooltip={
                            'Unique code or ticket symbol your company uses on any digital asset exchange or trading platform (if applicable).'
                        }
                    />
                    <FormTextField
                        field={fieldName('exchanges')}
                        label={'Exchange(s)'}
                        sideBySide
                    />
                </div>
            )}

            <FormMultiSelectField
                fieldName={fieldName('operationRegionIds')}
                options={selectOptions?.regionsOfOperation ?? []}
                label={'Regions of Business Operations'}
                tooltip={
                    'Countries or regions where your business actively operates or offers services.'
                }
            />
            <RadioButtons
                options={['Yes', 'No', 'NA']}
                fieldname={fieldName('bLicenseRequired')}
                label="Does your business require a license?"
            />

            <FormTextField
                field={fieldName('additionalLicensingInfo')}
                label={
                    'Any relevant details about your company’s regulatory status. Example: pending license applications, partnerships with licensed entities, or operational exemptions.'
                }
                labelExtraInfo="Optional"
                tooltip="Use this space to provide any explantion or clarification regarding the entity's legal status, registrations, jurisdiction of operation etc."
            />
            <FormTextField
                field={fieldName('primaryRegulator')}
                label={'Primary Regulator - Example: FCA, SEC, OCC, FINMA, MAS'}
                labelExtraInfo="Optional"
                tooltip="The name of the main regulatory authority that oversees your company’s financial or crypto-related activities."
            />
            <FormTextField
                field={fieldName('licenseNumber')}
                label={'License Number'}
                labelExtraInfo="Optional"
                tooltip="License number issued by your primary regulator"
            />

            <div className={`FormBox`}>
                <div className="FormLabel">
                    <label>
                        If you are considered a FINANCIAL INSTITUTION in the jurisdiction in which
                        you operate or are registered, please complete this form
                        <span className="Optional"> - optional</span>
                    </label>
                </div>
                <a href="/Ibanera-Financial-Institution-Form.pdf" download>
                    <Button type="button" style={{ marginBottom: 10 }} variety="full">
                        Download form
                    </Button>
                </a>
                <UploadField
                    fieldName={fieldName('financialInstitutionFormFileName')}
                    displayError={true}
                    multi={false}
                    jsonField={true}
                />
            </div>
            <FormMultiSelectField
                fieldName={fieldName('businessActivityIds')}
                options={selectOptions?.businessActivities ?? []}
                label={'Main Business Activity'}
            />
            <FormTextField
                type="textarea"
                field={fieldName('web3ChainsAndAddresses')}
                label={
                    'If the entity expects to utilize Web3 for transactions, provide blockchain and blockchain addresses that will be used when conducting business'
                }
                labelExtraInfo="Optional"
            />
            <FormMultiSelectField
                fieldName={fieldName('sourceOfFundsIds')}
                options={selectOptions?.fundingSources ?? []}
                label={'Please indicate the source of funding for the entity, check all that apply'}
            />

            {otherFundsSelected && (
                <FormTextField
                    field={fieldName('sourceOfFundsOther')}
                    label={'Other Source of Funds'}
                />
            )}
            <FormTextField
                type="textarea"
                field={fieldName('activeBanks')}
                label={'Name of bank for which the business currently holds an active account'}
            />
            <SelectOptionRadioButton
                options={selectOptions?.transactionVolumes ?? []}
                fieldname={fieldName('yearlyTransactionsId')}
                label="Anticipated yearly number of transactions"
            />
            <SelectOptionRadioButton
                options={selectOptions?.monthlyUsdEquivalents ?? []}
                fieldname={fieldName('monthlyUsdValueId')}
                label="Anticipated monthly US dollar equivalent"
            />

            <div style={{ marginTop: 10, display: isPortlProduct ? 'block' : 'none' }}>
                <FormTextField
                    field={fieldName('BusinessAndIndustryType')}
                    label={
                        'Business & Industry Type - Example: Dao, Defi, Exchange, OTC Desk, Metaverse, NFT project, Marketplace'
                    }
                    tooltip={'The industry your company operates in.'}
                />
                <FormTextField
                    type="textarea"
                    field={fieldName('CryptoCurrencyOffered')}
                    label={'Which cryptocurrencies will you offer?'}
                />
                <FormTextField
                    field={fieldName('URLs')}
                    label={'URLs'}
                    tooltip={' The primary website(s) associated with your business'}
                />
                <FormTextField
                    field={fieldName('ProcessingTraffic')}
                    label={'Processing Traffic – Countries Where Products/Services Are Bought'}
                    tooltip={'List of the countries where your end customers are located.'}
                />
                <FormTextField
                    type="textarea"
                    field={fieldName('WalletAddresses')}
                    label={'Account Country and Currencies or Cryptocurrency Wallet Address(es)'}
                />
                <FormTextField
                    field={fieldName('Director_ShareholderLocation')}
                    label={'Director/Shareholder Location (Country)'}
                    tooltip={'Country of residence for your company’s director/shareholder.'}
                />
                <FormTextField
                    field={fieldName('CompanyAuthorizedRepresentative')}
                    label={'Company’s Authorized Representative'}
                    tooltip={
                        'Name of the person officially designated to represent the company during onboarding and communications.'
                    }
                />
                <RadioButtons
                    options={['Yes', 'No']}
                    fieldname={fieldName('bCardPayment')}
                    label="Are You Currently Accepting Card Payments?"
                />
                <FormTextField
                    field={fieldName('ProviderName')}
                    label={'If yes, please provide the name of the provider.'}
                    tooltip={
                        'Name of your payment processor or acquiring bank for card transactions.'
                    }
                />
                <FormTextField
                    field={fieldName('ProjectedAnnualTurnover')}
                    label={'Projected Annual Turnover'}
                    tooltip={'Estimate of your expected revenue over the next 12 months'}
                />
                <FormTextField
                    field={fieldName('AverageTransactionValue')}
                    label={'Average Transaction Value'}
                    tooltip={'The typical value of a single customer transaction.'}
                />
                <FormTextField
                    field={fieldName('MinMaxTransactionValues')}
                    label={'Minimum and Maximum Transaction Values'}
                    tooltip={
                        'The smallest and largest amounts customers are expected to spend in a single transaction'
                    }
                />
                <FormTextField
                    field={fieldName('ChargeBack')}
                    label={'Chargeback Values (Over 6 Months)'}
                    tooltip={'Historical or expected values related to chargebacks'}
                />
                <FormTextField
                    field={fieldName('RefundValues')}
                    label={'Refund Values (Over 6 Months)'}
                    tooltip={'The average or total value of refunds issued.'}
                />
                <RadioButtons
                    options={['Yes', 'No']}
                    fieldname={fieldName('bOwnTokenSale')}
                    label="Do you want to list your own token for sale?"
                />
                <p style={{ fontWeight: 600 }}>If Yes, please provide the following details.</p>
                <FormTextField
                    type="textarea"
                    field={fieldName('ExecutiveSummaryCrypto')}
                    label={'Executive summary of the cryptocurrency'}
                />
                <div className={`FormBox`}>
                    <div className="FormLabel">
                        <label>
                            Diagram of the cryptocurrency’s technical architecture, characteristics,
                            and flow of funds
                        </label>
                    </div>
                    <UploadField
                        fieldName={fieldName('CryptoTechnicalArchitecture')}
                        displayError={true}
                        multi={false}
                        jsonField={true}
                    />
                </div>
                <div className={`FormBox`}>
                    <div className="FormLabel">
                        <label>
                            Formal regulatory determination and/or legal memorandum in support of
                            the cryptocurrency’s non-securities status
                        </label>
                    </div>
                    <UploadField
                        fieldName={fieldName('FormalRegulatory')}
                        displayError={true}
                        multi={false}
                        jsonField={true}
                    />
                </div>
                <FormTextField
                    type="textarea"
                    field={fieldName('BusinessPlan')}
                    label={'Business plan (should include description of blockchain used)'}
                />
                <RadioButtons
                    options={['Yes', 'No']}
                    fieldname={fieldName('bNFTSale')}
                    label="Will you be offering NFT sales?"
                />
                <p style={{ fontWeight: 600 }}>If Yes, please provide the following details.</p>
                <FormTextField
                    type="textarea"
                    field={fieldName('ExecutiveSummaryCrypto')}
                    label={'Executive summary of the cryptocurrency'}
                />
                <FormTextField
                    field={fieldName('ExecutiveSummaryNFT')}
                    label={
                        'Executive summary of the NFT project, technical architecture and blockchain used'
                    }
                    tooltip={
                        'High-level overview of your NFT project, including its purpose, target audience, and core features.'
                    }
                />
                <FormTextField
                    field={fieldName('NFTDynamic')}
                    label={'Are the NFTs dynamically minted at time of purchase or pre-minted?'}
                    tooltip={
                        "Clarify whether NFTs are created ('minted') on-demand when a user makes a purchase (dynamic minting) or if they are minted in advance and stored in a wallet awaiting distribution (pre-minted)."
                    }
                />
                <FormTextField
                    field={fieldName('NFTCustody')}
                    label={
                        'Will you manage NFT custody and settlement to purchaser or do you want Ibanera to custody and settle the NFTs to purchasers?'
                    }
                    tooltip={
                        'Who will be responsible for securely holding (custody) and delivering (settlement) the NFTs to the buyers.'
                    }
                />
            </div>
        </>
    );
};

const fieldName = (name: string) => `operationsInformation.${name}`;
